import { useEffect, useMemo, useState } from 'react';
import { Search, X } from 'lucide-react';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import {
  useChatMessageSearch,
  CHAT_SEARCH_MAX_QUERY_LENGTH,
  CHAT_SEARCH_MIN_QUERY_LENGTH,
} from '../hooks/useChatMessageSearch';
import { chatHtmlToPlainText } from '../utils';
import { SenderFilter } from './SenderFilter';
import type { ChatChannelMemberResponse } from '@/lib/api';

interface ChannelSearchPanelProps {
  channelId: string | null;
  /** Name shown in the placeholder, e.g. "Search in #general". */
  channelLabel: string;
  /**
   * Channel members — suggestions for the "From" filter, not a limit on it. The
   * filter also accepts a typed handle, so senders past the loaded member page
   * and people who have left the channel stay reachable.
   */
  members?: ChatChannelMemberResponse[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Scroll the conversation to this message. Optional — without it the results
   * are a read-only list (the embedded guild pane has no jump plumbing).
   */
  onSelectResult?: (messageId: string) => void;
}

/** Compact absolute timestamp — search results span history, so relative ages read poorly. */
function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Splits `text` on each case-insensitive occurrence of any search term so the
 * matched words can be marked. Terms come from the user's own query, and the
 * pieces are rendered as React children (never as HTML), so there is no
 * injection path here.
 */
function highlightSegments(text: string, terms: string[]): Array<{ text: string; hit: boolean }> {
  const escaped = terms
    .filter((t) => t.length > 0)
    .map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  if (escaped.length === 0) return [{ text, hit: false }];
  const source = `(${escaped.join('|')})`;
  // `split` with a single capture group interleaves the captured matches into
  // the result, so odd indices are exactly the hits. Testing each piece with a
  // /g/ regex instead would be wrong — lastIndex carries between calls.
  return text
    .split(new RegExp(source, 'gi'))
    .map((piece, i) => ({ text: piece, hit: i % 2 === 1 }))
    .filter((seg) => seg.text.length > 0);
}

/**
 * In-channel message search: full-text over message content, a "From" filter on
 * one sender, or both together. Either filter stands alone, so picking a sender
 * with an empty box lists everything they wrote here. Clicking a result closes
 * the panel and jumps to the message when it's inside the loaded window.
 */
export function ChannelSearchPanel({
  channelId,
  channelLabel,
  members,
  open,
  onOpenChange,
  onSelectResult,
}: ChannelSearchPanelProps) {
  const [rawQuery, setRawQuery] = useState('');
  const [senderFilter, setSenderFilter] = useState<string | null>(null);
  const query = useDebouncedValue(rawQuery, 250);

  // Start each visit from a clean slate rather than reopening onto a stale
  // result set from a previous channel.
  useEffect(() => {
    if (!open) {
      setRawQuery('');
      setSenderFilter(null);
    }
  }, [open]);

  const { results, total, isLoading, isError, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useChatMessageSearch(channelId, query, senderFilter, open);

  const trimmed = query.trim();
  const hasTerms = trimmed.length >= CHAT_SEARCH_MIN_QUERY_LENGTH;
  const isQueryable = hasTerms || !!senderFilter;
  const isDebouncing = rawQuery.trim() !== trimmed;
  // Only the text terms get highlighted; a sender filter has nothing to mark.
  const terms = useMemo(
    () => (hasTerms ? trimmed.split(/\s+/).filter(Boolean) : []),
    [trimmed, hasTerms],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-0 overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
          <Search className="h-4 w-4 text-primary flex-shrink-0" />
          <DialogTitle className="text-base font-semibold">Search messages</DialogTitle>
        </div>
        <DialogDescription className="sr-only">
          Search this conversation by message text, by sender, or both.
        </DialogDescription>

        <div className="px-4 pt-3 pb-2">
          <div className="relative">
            <input
              type="search"
              value={rawQuery}
              onChange={(e) => setRawQuery(e.target.value)}
              maxLength={CHAT_SEARCH_MAX_QUERY_LENGTH}
              // The panel is only mounted-open on demand, so grabbing focus here
              // is what the user asked for by opening it.
              autoFocus
              placeholder={`Search in ${channelLabel}`}
              aria-label={`Search messages in ${channelLabel}`}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 pr-8 text-sm outline-none focus:ring-2 focus:ring-primary/40"
            />
            {rawQuery.length > 0 && (
              <button
                type="button"
                onClick={() => setRawQuery('')}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-xs text-muted-foreground flex-shrink-0">From</span>
            <SenderFilter members={members ?? []} value={senderFilter} onChange={setSenderFilter} />
          </div>
          {isQueryable && !isLoading && !isError && (
            <p className="text-[11px] text-muted-foreground mt-2">
              {total} {total === 1 ? 'match' : 'matches'}
            </p>
          )}
        </div>

        <div className="max-h-[55vh] overflow-y-auto border-t border-border">
          {!isQueryable ? (
            <div className="text-center py-10 px-6">
              <Search className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">
                Type at least {CHAT_SEARCH_MIN_QUERY_LENGTH} characters to search
              </p>
              <p className="text-xs text-muted-foreground/70 mt-1">
                Or pick someone under “From” to see everything they wrote here.
              </p>
            </div>
          ) : isLoading || isDebouncing ? (
            <p className="text-sm text-muted-foreground text-center py-10">Searching…</p>
          ) : isError ? (
            <p className="text-sm text-destructive text-center py-10">
              Couldn&apos;t run that search. Try again.
            </p>
          ) : results.length === 0 ? (
            <div className="text-center py-10 px-6">
              <p className="text-sm text-muted-foreground">No messages found</p>
              <p className="text-xs text-muted-foreground/70 mt-1">
                {hasTerms
                  ? 'Try a different word — search matches whole words, not partial ones.'
                  : 'They haven’t posted in this conversation.'}
              </p>
            </div>
          ) : (
            <ul className="py-1">
              {results.map((message) => {
                const plain = chatHtmlToPlainText(message.content);
                const row = (
                  <>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium truncate">
                        {message.senderName || message.senderId}
                      </span>
                      <span className="text-[11px] text-muted-foreground flex-shrink-0 tabular-nums">
                        {formatTimestamp(message.createdAt)}
                      </span>
                    </div>
                    <p className="text-[13px] text-foreground/80 line-clamp-2 mt-0.5">
                      {highlightSegments(plain, terms).map((seg, i) =>
                        seg.hit ? (
                          <mark key={i} className="bg-primary/25 text-foreground rounded-sm px-0.5">
                            {seg.text}
                          </mark>
                        ) : (
                          <span key={i}>{seg.text}</span>
                        ),
                      )}
                    </p>
                  </>
                );

                return (
                  <li key={message.messageId}>
                    {onSelectResult ? (
                      <DialogClose asChild>
                        <button
                          type="button"
                          onClick={() => onSelectResult(message.messageId)}
                          className="w-full text-left px-4 py-2.5 hover:bg-muted/60 transition-colors"
                        >
                          {row}
                        </button>
                      </DialogClose>
                    ) : (
                      <div className="px-4 py-2.5">{row}</div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          {hasNextPage && (
            <div className="p-2 border-t border-border">
              <button
                type="button"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="w-full py-2 text-sm text-primary hover:bg-muted/60 rounded-lg transition-colors disabled:opacity-50"
              >
                {isFetchingNextPage ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
